import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useScramble } from 'use-scramble';

interface TopLanguageProps extends RewindSlideProps {
  topLanguage: string;
  isPrimaryTopLanguage: boolean;
}

const TopLanguage: FunctionComponent<TopLanguageProps> = (props) => {
  const { topLanguage, isPrimaryTopLanguage, active = false } = props;

  const translations: Record<string, string> = useMemo(() => {
    return {
      English: 'Your top language was',
      French: 'Votre langue principale était le français',
      Swedish: 'Ditt favoritspråk var svenska',
      Italian: 'La lingua in cui hai usufruito la maggior parte dei contenuti è stata l\'italiano',
      German: 'Ihre meistgenutzte Sprache war Deutsch',
      Korean: '귀하의 가장 많이 사용된 언어는 한국어입니다',
      Danish: 'Dit mest sete sprog var dansk',
      Cantonese: '你選用最多的語言是廣東話',
      Norwegian: 'Ditt favorittspråk var norsk',
      Japanese: 'あなたの最多閲覧言語は日本語でした',
      Mandarin: '您的首选语言是中文普通话',
      Spanish: 'Tu idioma top fue el español',
      Arabic: 'لغتك المفضّلة كانت العربية',
      Finnish: 'Eniten katsomasi sisällön kieli: Finnish',
      Hindi: 'आपने हिंदी में सबसे अधिक सामग्री देखी',
      Russian: 'Языком, который вы чаще всего выбирали при просмотре, был русский',
      'French Canadian': 'Votre langue la mieux classée était le français Canadien',
      Portuguese: 'Seu idioma favorito foi Português',
      Polish: 'Najczęściej wybieranym przez ciebie językiem był Polski',
      Icelandic: 'Vinsælasta tungumálið þitt var íslenska',
      Hebrew: 'השפה העיקרית שלכם היא עברית',
      Vietnamese: 'Ngôn ngữ hàng đầu của bạn là tiếng Việt',
      Dutch: 'Jouw populairste taal was Nederlands',
      Greek: 'Η προτιμότερη γλώσσα σας ήταν τα ελληνικά',
      Thai: 'ภาษาหลักที่ท่านเลือกชมคือภาษาไทย',
      Ukrainian: 'Мовою, яку ви найчастіше обирали під час перегляду, була українська',
      Farsi: 'زبان نخست شما فارسی بود',
      Afrikaans: 'Jou voorkeurtaal was Afrikaans',
      Romanian: 'Limba română este limba programelor cele mai vizionate de dumneavoastră',
      Mongolian: 'Та Монгол хэлээр хамгийн их үзсэн байна.',
      Croatian: 'Hrvatski je bio vaš najgledaniji jezik.',
      Serbian: 'Ваш главни језик је био српски',
      Tamil: 'காணொலிகளுக்கு நீங்கள் தெரிவு செய்யும் முதன்மை மொழி தமிழ்',
      Bangla: 'আপনার শীর্ষ ভাষা ছিল বাংলা',
      Punjabi: 'ਤੁਹਾਡੀ ਚੋਟੀ ਦੀ ਭਾਸ਼ਾ ਪੰਜਾਬੀ ਸੀ',
      Indonesian: 'Bahasa utama Anda adalah Bahasa Indonesia',
      Urdu: 'آپ کی ٹاپ زبان اردو تھی',
      Khmer: 'ភាសាទស្សនាច្រើនជាងគេរបស់អ្នកគឺ ខ្មែរ',
      Malayalam: 'നിങ്ങള്‍ ഏറ്റവുമധികം പിന്തുടര്‍ന്ന ഭാഷ മലയാളം',
      Turkish: 'Tercih ettiğiniz dil Türkçe idi',
    };
  }, []);

  const secondaryTranslations: Record<string, string> = useMemo(() => {
    return {
      English: 'Your top language (other than English) was',
      French: 'Votre langue principale (en dehors de l\'anglais) était le français',
      Swedish: 'Ditt favoritspråk (förutom engelska) var svenska',
      Italian: 'La lingua diversa dall\'inglese in cui hai usufruito la maggior parte dei contenuti è stata l\'italiano',
      German: 'Ihre meistgenutzte Sprache (außer English) war Deutsch',
      Korean: '귀하의 가장 많이 사용된 언어(영어 제외)는 한국어입니다',
      Danish: 'Dit mest sete sprog (end engelsk) var dansk',
      Cantonese: '你選用最多的語言（除英文外）是廣東話',
      Norwegian: 'Ditt favorittspråk (bortsett fra engelsk) var norsk',
      Japanese: 'あなたの(英語以外の) 最多閲覧言語は日本語でした',
      Mandarin: '您（英语以外）的首选语言是中文普通话',
      Spanish: 'Tu idioma top (aparte del inglés) fue el español',
      Arabic: 'لغتك المفضّلة (غير الإنكليزية) كانت العربية',
      Finnish: 'Eniten katsomasi sisällön kieli (muu kuin englanti): Finnish',
      Hindi: 'आपने हिंदी में (अंग्रेज़ी के अलावा) सबसे अधिक सामग्री देखी',
      Russian: 'Языком, который вы чаще всего выбирали при просмотре (кроме английского), был русский',
      'French Canadian': 'Votre langue la mieux classée (autre que l\'anglais) était le français Canadien',
      Portuguese: 'Seu idioma favorito (sem ser o inglês) foi Português',
      Polish: 'Najczęściej wybieranym przez ciebie językiem (innym niż angielski) był Polski',
      Icelandic: 'Vinsælasta tungumálið þitt (utan ensku) var íslenska',
      Hebrew: 'השפה העיקרית שלכם (חוץ מאנגלית) היא עברית',
      Vietnamese: 'Ngôn ngữ hàng đầu của bạn (mà không phải là tiếng Anh) là tiếng Việt',
      Dutch: 'Jouw populairste taal (anders dan Engels) was Nederlands',
      Greek: 'Η προτιμότερη γλώσσα σας (πλην των αγγλικών) ήταν τα ελληνικά',
      Thai: 'ภาษาหลักที่ท่านเลือกชม (ซึ่งไม่ใช่ภาษาอังกฤษ) คือภาษาไทย',
      Ukrainian: 'Мовою, яку ви найчастіше обирали під час перегляду (окрім англійської), була українська',
      Farsi: 'زبان نخست شما (به غیر از انگلیسی) فارسی بود',
      Afrikaans: 'Jou voorkeurtaal (benewens Engels) was Afrikaans',
      Romanian: 'Limba română este limba programelor cele mai vizionate de dumneavoastră (altele decât cele în limba engleză)',
      Mongolian: 'Та Монгол хэлээр (Англиас бусад хэл) хамгийн их үзсэн байна.',
      Croatian: 'Hrvatski je (pored engleskog) bio vaš najgledaniji jezik.',
      Serbian: 'Ваш главни језик (oсим енглеског) је био српски',
      Tamil: 'காணொலிகளுக்கு (ஆங்கிலம் தவிர) நீங்கள் தெரிவு செய்யும் முதன்மை மொழி தமிழ்',
      Bangla: 'আপনার শীর্ষ ভাষা ছিল (ইংরেজি ছাড়়া) বাংলা',
      Punjabi: 'ਤੁਹਾਡੀ ਚੋਟੀ ਦੀ ਭਾਸ਼ਾ (ਅੰਗਰੇਜ਼ੀ ਤੋਂ ਇਲਾਵਾ) ਪੰਜਾਬੀ ਸੀ',
      Indonesian: 'Bahasa utama Anda (selain bahasa Inggris) adalah Bahasa Indonesia',
      Urdu: 'آپ کی ٹاپ زبان (انگلش کے علاوہ) اردو تھی',
      Khmer: 'ភាសាទស្សនាច្រើនជាងរបស់អ្នក (ក្រៅពីភាសាអង់គ្លេស) គឺ ខ្មែរ',
      Malayalam: 'നിങ്ങള്‍ ഏറ്റവുമധികം പിന്തുടര്‍ന്ന (ഇംഗ്ലീഷ് ഇതര) ഭാഷ മലയാളം',
      Turkish: 'Tercih ettiğiniz (İngilizce dışındaki) dil Türkçe idi',
    };
  }, []);

  const languangeNames: Record<string, string> = useMemo(() => {
    return {
      English: 'English',
      French: 'français',
      Swedish: 'svenska',
      Italian: 'l\'italiano',
      German: 'Deutsch',
      Korean: '한국어',
      Danish: 'dansk',
      Cantonese: '廣東話',
      Norwegian: 'norsk',
      Japanese: '日本語',
      Mandarin: '中文普通话',
      Spanish: 'español',
      Arabic: 'العربية',
      Finnish: 'Finnish',
      Hindi: 'हिंदी',
      Russian: 'русский',
      'French Canadian': 'français Canadien',
      Portuguese: 'Português',
      Polish: 'Polski',
      Icelandic: 'íslenska',
      Hebrew: 'עברית',
      Vietnamese: 'Việt',
      Dutch: 'Nederlands',
      Greek: 'τα ελληνικά',
      Thai: 'ภาษาไทย',
      Ukrainian: 'українська',
      Farsi: 'فارسی',
      Afrikaans: 'Afrikaans',
      Romanian: 'Limba română',
      Mongolian: 'Монгол',
      Croatian: 'Hrvatski',
      Serbian: 'српски',
      Tamil: 'தமிழ்',
      Bangla: 'বাংলা',
      Punjabi: 'ਪੰਜਾਬੀ',
      Indonesian: 'Bahasa Indonesia',
      Urdu: 'اردو',
      Khmer: 'ខ្មែរ',
      Malayalam: 'മലയാളം',
      Turkish: 'Türkçe',
    };
  }, []);

  let scrambledText = '';
  let initialText = '';
  let languageNameText = '';

  if (isPrimaryTopLanguage) {
    if (topLanguage === 'English' || !(topLanguage in translations)) {
      scrambledText = `${translations.English}`;
      initialText = `${translations.English} ${topLanguage}`;
    } else {
      scrambledText = `${translations.English}`;
      initialText = translations[topLanguage];
      languageNameText = languangeNames[topLanguage];
    }
  } else if (topLanguage in translations) {
    scrambledText = `${secondaryTranslations.English}`;
    initialText = secondaryTranslations[topLanguage];
    languageNameText = languangeNames[topLanguage];
  } else {
    scrambledText = `${secondaryTranslations.English}`;
    initialText = `${secondaryTranslations.English} ${topLanguage}`;
  }

  // Updates to this state will trigger the scrambling animation
  const [text, setText] = useState<string>(initialText);

  // Setting this to true will instantly updates the text without animating
  const [resetScramble, setResetScramble] = useState<boolean>(true);

  const { ref, replay } = useScramble({
    playOnMount: false,
    text,
    speed: 1,
    tick: resetScramble ? 1 : 5,
    step: resetScramble ? 10 : 5,
    overflow: true,
    scramble: resetScramble ? 0 : 30,
    seed: 0,
    chance: resetScramble ? 0 : 0.6,
    range: [65, 90],
    overdrive: false,
    onAnimationEnd: () => {
      if (ref.current.innerText.indexOf('Your top language') !== -1) {
        ref.current.innerHTML = `${scrambledText} <span class="text-dark-tangerine">${topLanguage}</span>`;
      } else {
        const styledInitalText = initialText.replace(new RegExp(`(${languageNameText})`, 'g'), '<span class="text-dark-tangerine">$1</span>');
        ref.current.innerHTML = styledInitalText;
      }
    },
  });

  useEffect(() => {
    if (active) {
      const timeoutHandler = setTimeout(() => {
        setResetScramble(false);
        setText(`${scrambledText} ${topLanguage}`);
      }, 3000);
      return () => {
        clearTimeout(timeoutHandler);
      };
    }

    return () => {
      setResetScramble(true);
      setText(initialText);
    };
  }, [active, ref, replay, topLanguage, secondaryTranslations, scrambledText, initialText]);

  return (
    <h2
      ref={ref}
      id="test"
      className="text-2xl md:text-3xl lg:text-4xl w-full max-w-[550px] md:max-w-[700px] lg:max-w-[900px] m-auto slide-up min-h-[160px]"
    >
      {' '}
    </h2>
  );
};

export default TopLanguage;
